import type { DirectionPayload } from '@/features/settings/api/useDirectionTemplates'
import { MAX_DIRECTION_POINT_NAME, dayProblem, type DayProblem } from '@/features/settings/directionRules'
import type {
  DirectionTemplate,
  LegPointsPayload,
  LegRoutePoint,
  RoutePointKind,
} from '@/types/domain'

/* The route of a leg that has no stored points yet, as the leg drawer's
 * «Маршрут» tab drafts it (S-10) — and the ONE rule for which request, if any,
 * that draft becomes once the leg write has landed.
 *
 * THE CHAIN IS THE LEG'S ENDS. There are no separate «Откуда/Куда» inputs: the
 * first and last point are the ends, the leg write carries them, and the chain
 * write that follows (when there is one) calls `SetLegEnds` in its own
 * transaction with the same two — so the two never disagree.
 *
 * After the leg write, exactly one of three:
 *
 *  - a picked template left untouched → `apply-template`: the server reads the
 *    template itself (AD-15), so the copy is the catalogue's, not this
 *    client's rendition of it;
 *  - a chain edited after picking, or typed by hand, that carries days (two
 *    points or more) → `PUT …/points`, days kept;
 *  - two ends with no days → nothing: the ends are all there is.
 *
 * Once a leg HAS stored points this draft is not used: the stored chain is
 * edited in its own editor with immediate writes, and the drawer only offers a
 * template to replace it.
 */

/** One point as the drawer types it. `days` is a STRING for
 *  `DirectionFormSheet`'s reason: `Number('')` is 0, a transition the position
 *  would advance through the instant it began. */
export interface ChainPointDraft {
  key: string
  name: string
  /** ISO 3166-1 alpha-2, or `''`. */
  country: string
  kind: RoutePointKind
  /** Days INTO this point; never read on the first. */
  days: string
}

export interface ChainDraft {
  /** The template the points were copied from, `''` for none. */
  templateId: string
  /** The points are the template's as copied, untouched — which is what makes
   *  the save `apply-template` rather than a points PUT. */
  fromTemplate: boolean
  points: ChainPointDraft[]
}

/** A leg point's name bound (`maxShortTextLength`), the ends' own. */
export const MAX_LEG_POINT_NAME = 200

/** The ceiling the server enforces (`maxLegPoints`). */
export const MAX_LEG_POINTS = 20

let pointSeq = 0

function nextKey(): string {
  pointSeq += 1
  return `point-${pointSeq}`
}

export function emptyChainPoint(): ChainPointDraft {
  return { key: nextKey(), name: '', country: '', kind: 'waypoint', days: '' }
}

/** The two ends as a chain of two — what a leg with no stored points opens
 *  with, and what «+ Новый» starts from. */
export function chainFromEnds(ends: {
  origin: string
  originCountry: string
  destination: string
  destinationCountry: string
}): ChainDraft {
  return {
    templateId: '',
    fromTemplate: false,
    points: [
      { ...emptyChainPoint(), name: ends.origin, country: ends.originCountry },
      { ...emptyChainPoint(), name: ends.destination, country: ends.destinationCountry },
    ],
  }
}

/** A template's points, copied into the draft so the reader sees what the
 *  apply will produce. The SAVE still asks the server to copy them. */
export function chainFromTemplate(template: DirectionTemplate): ChainDraft {
  return {
    templateId: template.id,
    fromTemplate: true,
    points: template.points.map((point, index) => ({
      key: nextKey(),
      name: point.name,
      country: point.country,
      kind: point.kind ?? 'waypoint',
      days: index === 0 || point.daysFromPrevious === null ? '' : String(point.daysFromPrevious),
    })),
  }
}

/** A stored chain in the draft's shape — the source of «Сохранить как шаблон»
 *  on a leg that already has points. */
export function chainFromStored(points: readonly LegRoutePoint[]): ChainPointDraft[] {
  return points.map((point) => ({
    key: point.id,
    name: point.name,
    country: point.country ?? '',
    kind: point.kind ?? 'waypoint',
    days: point.plannedDaysFromPrevious === undefined ? '' : String(point.plannedDaysFromPrevious),
  }))
}

/** The leg's two ends, read off the chain. */
export function chainEnds(chain: ChainDraft): {
  origin: string
  originCountry: string
  destination: string
  destinationCountry: string
} {
  const first = chain.points.at(0)
  const last = chain.points.at(-1)
  return {
    origin: first?.name.trim() ?? '',
    originCountry: first?.country ?? '',
    destination: last?.name.trim() ?? '',
    destinationCountry: last?.country ?? '',
  }
}

/** Every point after the first carries whole days. */
function everyDay(points: readonly ChainPointDraft[]): boolean {
  return points.length >= 2 && points.slice(1).every((point) => dayProblem(point.days) === undefined)
}

/** The chain request a save sends after the leg write, or null for none. */
export type ChainStep =
  | { kind: 'apply'; templateId: string }
  | { kind: 'points'; payload: LegPointsPayload }

export function chainStep(chain: ChainDraft | null): ChainStep | null {
  if (!chain) return null
  if (chain.templateId !== '' && chain.fromTemplate) {
    return { kind: 'apply', templateId: chain.templateId }
  }
  if (!everyDay(chain.points)) return null
  return {
    kind: 'points',
    payload: {
      points: chain.points.map((point, index) => ({
        name: point.name.trim(),
        country: point.country.trim() === '' ? undefined : point.country.trim(),
        kind: point.kind,
        plannedDaysFromPrevious: index === 0 ? undefined : Number(point.days.trim()),
        // A leg with no stored points has recorded no arrival yet.
        reached: false,
      })),
    },
  }
}

/** What the drawer refuses before «Готово», per point. The two ENDS may be
 *  blank here — the save bar names them — but a point between them may not:
 *  a nameless stop is no stop. A chain of three or more needs whole days on
 *  every point after the first (0054); a chain of two may go without. */
export interface ChainProblems {
  invalid: boolean
  name: Map<string, 'common.form.required' | 'common.form.tooLong'>
  days: Map<string, DayProblem>
}

export function chainProblems(chain: ChainDraft): ChainProblems {
  const name = new Map<string, 'common.form.required' | 'common.form.tooLong'>()
  const days = new Map<string, DayProblem>()
  const last = chain.points.length - 1

  chain.points.forEach((point, index) => {
    const trimmed = point.name.trim()
    if (trimmed.length > MAX_LEG_POINT_NAME) name.set(point.key, 'common.form.tooLong')
    else if (trimmed === '' && index !== 0 && index !== last) name.set(point.key, 'common.form.required')

    if (index === 0) return
    const problem = dayProblem(point.days)
    const optional = chain.points.length === 2 && point.days.trim() === ''
    if (problem && !optional) days.set(point.key, problem)
  })

  return { invalid: name.size > 0 || days.size > 0, name, days }
}

/** Why the chain cannot become a template, or null when it can. A template
 *  needs days on every point after the first, and its point names are capped
 *  at 120 where a leg's take 200. */
export type TemplateBlock = 'noDays' | 'nameTooLong'

export function templateBlock(points: readonly ChainPointDraft[]): TemplateBlock | null {
  if (!everyDay(points)) return 'noDays'
  if (points.some((point) => point.name.trim().length > MAX_DIRECTION_POINT_NAME)) return 'nameTooLong'
  return null
}

/** «<first> — <last>», the name «Сохранить как шаблон» opens with. */
export function templateNameOf(points: readonly ChainPointDraft[]): string {
  const first = points.at(0)?.name.trim() ?? ''
  const last = points.at(-1)?.name.trim() ?? ''
  return first && last ? `${first} — ${last}` : first || last
}

/** «Сохранить как шаблон», ticked: the name the reader typed, or — until they
 *  type one (`edited`) — the chain's own «<first> — <last>». */
export interface TemplateNameDraft {
  name: string
  edited: boolean
}

/** The name a ticked «Сохранить как шаблон» creates — the reader's, or the
 *  chain's, which follows the chain as it is edited until the reader types. */
export function effectiveTemplateName(
  draft: TemplateNameDraft,
  points: readonly ChainPointDraft[],
): string {
  return draft.edited ? draft.name.trim() : templateNameOf(points)
}

/** The template create the save sends last. */
export function directionPayloadOf(
  name: string,
  points: readonly ChainPointDraft[],
): DirectionPayload {
  return {
    name: name.trim(),
    active: true,
    points: points.map((point, index) => ({
      name: point.name.trim(),
      country: point.country.trim().toUpperCase(),
      kind: point.kind,
      daysFromPrevious: index === 0 ? null : Number(point.days.trim()),
    })),
  }
}
